/**
 * Featured catalog item with its specification sheet — a wide hero card used at
 * the top of a category page (currently the Microfiber home page).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Plus, Download, FileText, Star, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export const FeaturedSpecProduct = ({ product, detailsTo, onAdd, onAddAndView }) => {
  const [shot, setShot] = useState(0);
  const gallery = product.galleryImages?.length ? product.galleryImages : [product.image];

  return (
    <section className="mb-10" data-testid={`featured-product-${product.sku}`}>
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
        <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
          Featured item
        </h2>
      </div>

      <div className="bg-white rounded-2xl border-2 border-emerald-200 shadow-sm overflow-hidden
        grid lg:grid-cols-[0.9fr_1.1fr]">
        {/* imagery */}
        <div className="bg-gradient-to-br from-slate-50 to-emerald-50/40 p-6 border-b lg:border-b-0
          lg:border-r border-slate-200">
          <Link to={detailsTo} className="block aspect-[4/3] bg-white rounded-xl border border-slate-200
            overflow-hidden" data-testid={`featured-image-link-${product.sku}`}>
            <img src={gallery[shot]} alt={product.name}
              className="w-full h-full object-contain p-4" loading="lazy" />
          </Link>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3">
              {gallery.map((g, i) => (
                <button key={i} type="button" onClick={() => setShot(i)}
                  className={`w-14 h-14 rounded-lg border-2 bg-white overflow-hidden transition-colors ${
                    i === shot ? "border-emerald-500" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`featured-thumb-${i}`}>
                  <img src={g} alt="" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* copy + specs */}
        <div className="p-6">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 mb-1.5">
            In stock · 3-day shipping · 240/CS
          </p>
          <Link to={detailsTo}
            className="block text-2xl sm:text-3xl font-extrabold text-slate-900 leading-[1.12]
              tracking-tight hover:text-emerald-700 transition-colors"
            data-testid={`featured-title-${product.sku}`}>
            {product.name}
          </Link>
          <p className="mt-1 text-xs font-bold text-slate-500">SKU {product.sku}</p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">{product.description}</p>

          {product.specHighlights?.length > 0 && (
            <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 border-y border-slate-200 py-3"
              data-testid={`featured-specs-${product.sku}`}>
              {product.specHighlights.map((s) => (
                <div key={s.label} className="flex items-start justify-between gap-3">
                  <dt className="text-[12px] text-slate-500">{s.label}</dt>
                  <dd className="text-[12.5px] font-bold text-slate-900 text-right">{s.value}</dd>
                </div>
              ))}
            </dl>
          )}

          <p className="mt-3 flex items-center gap-1.5 text-[12px] text-emerald-800">
            <Check className="w-3.5 h-3.5" /> No additional tariffs or sales tax on this product
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button onClick={onAddAndView}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid={`featured-add-view-${product.sku}`}>
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Add case to quote
            </Button>
            <Button onClick={onAdd} variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid={`featured-quick-add-${product.sku}`}>
              <Plus className="w-4 h-4 mr-1" /> Quick add
            </Button>
            {product.specSheet && (
              <a href={product.specSheet.file} target="_blank" rel="noopener noreferrer" download
                className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50
                  px-4 py-2 text-sm font-bold text-amber-800 hover:bg-amber-100 transition-colors"
                data-testid={`featured-spec-sheet-${product.sku}`}>
                <FileText className="w-4 h-4" /> {product.specSheet.label}
                <Download className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
          <Link to={detailsTo}
            className="mt-3 inline-block text-[12px] font-semibold text-emerald-700 hover:text-emerald-800"
            data-testid={`featured-details-${product.sku}`}>
            Full specifications &amp; laundering guidance →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSpecProduct;
